import React, { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { Divider, Layout, Text } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import Form from '../../../FormFields/Form';
import GooglePlaces_Autocomplete from '../../../Map/GooglePlacesAutocomplete';
import MapView from '../../../Map';
import SubmitButton from '../../../FormFields/SubmitButton';

import validation from '../../../../Config/validationRules';

import ThemeColor from '../../../../Theme/colors';
import ThemeStyle from '../../../../Theme/styles';

import useController from '../../../../Screens/Address/controller/addEditAddress';

import AddLabelForm from '../../../../Screens/Address/AddLabelForm';

import ModalPrompt from './ModalPrompt';
import useModalPrompt from './hooks/useModalPrompt';
import breakpoints from '../../../../Theme/styles/breakpoints';

let thisFormInitialValues = {};
const validationSchema = validation.createSchema({
  label: validation.fieldRequired('Address label'),
});
const formDefaultValues = { label: null };

const AddressDetails = ({ modalId }) => {
  const [data, setData] = useState<{
    selectedAddress: string;
    status: string;
    type?: string;
  }>();

  const status = data?.status;

  const {
    state,
    autoCompleteRef,
    onSearchItemPressed,
    onSubmit,
    formRef,
    formInitialValues,
    onRegionChangeComplete,
  } = useController(data || { navigation: '', route: {}, selectedAddress: {} });

  const { hideModalPrompt } = useModalPrompt();
  const { selectedAddress, submitting, existingError } = state;
  const location = {
    lat: selectedAddress?.latitude || 0,
    lng: selectedAddress?.longitude || 0,
  };

  if (formInitialValues) {
    thisFormInitialValues = formInitialValues;
  } else {
    thisFormInitialValues = formDefaultValues;
  }

  const onSubmitChanges = async (...args) => {
    const center = {
      latitude: location.lat,
      longitude: location.lng,
    };

    await onSubmit(center, ...args);
  };
  const onClose = () => hideModalPrompt(modalId, null);

  return (
    <ModalPrompt
      id={modalId}
      title={status === 'new' ? 'Add New Address' : 'Edit Address'}
      hasHeader={true}
      hasHeaderLine={true}
      closeOnDocumentClick={false}
      onBeforeShow={setData}
      onCloseButtonClick={onClose}
      mainContentWrapperStyle={styles.mainContentWrapperStyle}
    >
      <Layout style={[ThemeStyle.flex1]}>
        <Form
          innerRef={formRef}
          initialValues={thisFormInitialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmitChanges}
        >
          <AddLabelForm existingError={existingError} />

          <Divider style={ThemeStyle.divider} />

          <View style={ThemeStyle.flex2}>
            {/* Map Container */}

            <Layout style={ThemeStyle.spacingTopMedium}>
              {/* Search suggestion container */}
              <Layout style={styles.searchContainer}>
                <GooglePlaces_Autocomplete
                  testID="gpautocomplete"
                  ref={!submitting ? autoCompleteRef : null}
                  initialValue={selectedAddress?.address}
                  onSelected={onSearchItemPressed}
                  style={styles.googleplace_autocomplete}
                />

                <Divider style={ThemeStyle.divider} />
              </Layout>
            </Layout>
            {!lodashIsEmpty(selectedAddress) && (
              <View style={ThemeStyle.flex1}>
                <View
                  dataSet={{ media: ids.mapContainer }}
                  style={styles.mapContainer}
                >
                  <MapView
                    testID="mapViewAddEdit"
                    region={location}
                    onRegionChanged={onRegionChangeComplete}
                  />
                </View>
              </View>
            )}
          </View>
          <View
            style={[
              ThemeStyle.spacingTop,
              ThemeStyle.spacingBottomMedium,
              ThemeStyle.alignItemsCenter,
            ]}
          >
            <SubmitButton
              size="medium"
              style={styles.saveButton}
              disabled={lodashIsEmpty(selectedAddress.address)}
              customLoader
            >
              <View>
                {submitting ? (
                  <ActivityIndicator size="large" color={ThemeColor.white} />
                ) : (
                  <Text
                    status={
                      lodashIsEmpty(selectedAddress.address)
                        ? 'primary'
                        : 'control'
                    }
                    category="c1"
                  >
                    Confirm location and details
                  </Text>
                )}
              </View>
            </SubmitButton>
          </View>
        </Form>
      </Layout>
    </ModalPrompt>
  );
};

const { ids, styles } = StyleSheet.create({
  searchContainer: {
    flex: 1,
    zIndex: 1,
    left: 0,
    right: 0,
  },
  saveButton: {
    borderRadius: 5,
  },
  mainContentWrapperStyle: { maxHeight: '100dvh' },
  mapContainer: {
    marginTop: 20,
    borderRadius: 10,
    alignSelf: 'center',
    width: '100%',
    [`@media ${breakpoints.xs}`]: {
      height: 300,
    },
    [`@media ${breakpoints.sm}`]: {
      height: 320,
    },
    [`@media ${breakpoints.md}`]: {
      height: 340,
    },
    [`@media ${breakpoints.lg}`]: {
      height: 400,
    },
  },
  googleplace_autocomplete: {
    ...ThemeStyle.regular,
    fontSize: 12,
  },
});

export { formDefaultValues };
export default React.memo(AddressDetails);
